/* eslint-disable react-hooks/exhaustive-deps */
import { faCirclePlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FieldArray, FormikProps, getIn, useFormikContext } from "formik";
import React, { useEffect, useState } from "react";
import { Alert, Button, Card, Col, Row } from "react-bootstrap";

import { ISaleLandAndBuildingForm } from "../../../../config/interfaces";
import FASvgIcon from "../../../FASvgIcon";
import { NumericInputField, TextInputField } from "../../../fields";
import PreventEditComponent from "../../../PreventEditComponent";

const CGSaleLandAndBuildingBuyerForm: React.FC = () => {
  const { values, setFieldValue } = useFormikContext<ISaleLandAndBuildingForm>();
  const [errorMessage, setErrorMessage] = useState<string>("");

  useEffect(() => {
    let sum = values.BuyerDetails.slice(1).reduce(
      (acc, item) => acc + (parseFloat(item.Percentage) || 0),
      0
    );
    const firstBuyerPercentage = 100 - sum;
    setFieldValue(`BuyerDetails.${0}.Percentage`, firstBuyerPercentage);

    if (sum > 100) {
      setErrorMessage("Sum of percentage share should always be 100");
    } else if (firstBuyerPercentage === 0) {
      setErrorMessage("First buyer's percentage cannot be zero");
    } else {
      setErrorMessage("");
    }
  }, [values.BuyerDetails]);

  return (
    <section className="mb-5">
      <Card className="more-info-card shadow-none">
        <Card.Header>
          <Card.Title>Buyer Details</Card.Title>
        </Card.Header>
        <Card.Body>
          <section>
            {errorMessage && (
              <Alert variant="danger" className="mb-4 size-sm">
                <h5 className="alert-title">{errorMessage}</h5>
              </Alert>
            )}
            <FieldArray name="BuyerDetails">
              {({ push, remove, replace, form }) => (
                <>
                  {values?.BuyerDetails?.map((_, index) => (
                    <section className="mb-3 mb-sm-0" key={`buyer-${index}`}>
                      <BuyerForm
                        index={index}
                        form={form}
                        onRemove={(itemIdx: number) => {
                          if (values.BuyerDetails.length === 1) {
                            replace(itemIdx, {
                              BuyerName: "",
                              Amount: "",
                              Pan: "",
                              Percentage: "",
                              Aadhaar: ""
                            });
                          } else {
                            remove(itemIdx);
                          }

                          // setCurrentTime(new Date().getTime());
                        }}
                      />
                    </section>
                  ))}
                  <PreventEditComponent>
                    <Button
                      variant="link"
                      className="btn-add-link"
                      onClick={() =>
                        push({
                          BuyerName: "",
                          Pan: "",
                          Amount: "",
                          Percentage: "",
                          Aadhaar: ""
                        })
                      }
                      disabled={!(Number(values.BuyerDetails[0].Percentage) > 0)}
                    >
                      <FASvgIcon icon={faCirclePlus} />
                      Add more
                    </Button>
                  </PreventEditComponent>
                </>
              )}
            </FieldArray>
          </section>
        </Card.Body>
      </Card>
    </section>
  );
};

export default CGSaleLandAndBuildingBuyerForm;

type Props = {
  index: number;
  form: FormikProps<ISaleLandAndBuildingForm>;
  onRemove: (itemIdx: number) => void;
};

const BuyerForm: React.FC<Props> = (props: Props) => {
  const { index, onRemove, form } = props;

  const { setFieldValue, handleBlur, values, touched, errors } = form;

  return (
    <>
      <Row className="align-items-center">
        <Col sm={6} md={4} xl>
          <TextInputField
            label="Buyer Name"
            name={`BuyerDetails.${index}.BuyerName`}
            value={values.BuyerDetails[index].BuyerName}
            isRequired={true}
            onBlur={(e) => {
              handleBlur(e);
              setFieldValue(
                `BuyerDetails.${index}.BuyerName`,
                values.BuyerDetails[index].BuyerName.trim()
              );
            }}
            onChange={(value) => setFieldValue(`BuyerDetails.${index}.BuyerName`, value)}
            isInvalid={
              Boolean(getIn(errors, `BuyerDetails.${index}.BuyerName`)) &&
              getIn(touched, `BuyerDetails.${index}.BuyerName`)
            }
            errorMsg={getIn(errors, `BuyerDetails.${index}.BuyerName`)}
          />
        </Col>
        <Col sm={6} md={4} xl>
          <NumericInputField
            showRightIcon={true}
            label="Ownership Percentage"
            name={`BuyerDetails.${index}.Percentage`}
            value={values.BuyerDetails[index].Percentage}
            isRequired={true}
            onBlur={handleBlur(`BuyerDetails.${index}.Percentage`)}
            onChange={(value) => {
              setFieldValue(`BuyerDetails.${index}.Percentage`, value);
            }}
            isInvalid={
              Boolean(getIn(errors, `BuyerDetails.${index}.Percentage`)) &&
              getIn(touched, `BuyerDetails.${index}.Percentage`)
            }
            errorMsg={getIn(errors, `BuyerDetails.${index}.Percentage`)}
            isDisabled={index === 0}
          />
        </Col>

        <Col sm={6} md={4} xl>
          <NumericInputField
            label="Aadhaar"
            maxLength={12}
            name={`BuyerDetails.${index}.Aadhaar`}
            value={values.BuyerDetails[index].Aadhaar}
            isRequired={true}
            onBlur={handleBlur(`BuyerDetails.${index}.Aadhaar`)}
            onChange={(value) => {
              setFieldValue(`BuyerDetails.${index}.Aadhaar`, value);
            }}
            isInvalid={
              Boolean(getIn(errors, `BuyerDetails.${index}.Aadhaar`)) &&
              getIn(touched, `BuyerDetails.${index}.Aadhaar`)
            }
            errorMsg={getIn(errors, `BuyerDetails.${index}.Aadhaar`)}
            separator=""
          />
        </Col>
        <Col sm={6} md={4} xl>
          <TextInputField
            label="PAN"
            name={`BuyerDetails.${index}.Pan`}
            maxLength={10}
            value={values.BuyerDetails[index].Pan}
            isRequired={true}
            onBlur={(e) => {
              handleBlur(e);
              setFieldValue(`BuyerDetails.${index}.Pan`, values.BuyerDetails[index].Pan.trim());
            }}
            onChange={(value) => {
              setFieldValue(`BuyerDetails.${index}.Pan`, value.toUpperCase());
            }}
            isInvalid={
              Boolean(getIn(errors, `BuyerDetails.${index}.Pan`)) &&
              getIn(touched, `BuyerDetails.${index}.Pan`)
            }
            errorMsg={getIn(errors, `BuyerDetails.${index}.Pan`)}
          />
        </Col>

        <Col sm={6} md={4} xl>
          <NumericInputField
            label="Amount"
            showLeftIcon={true}
            name={`BuyerDetails.${index}.Amount`}
            value={values.BuyerDetails[index].Amount}
            isRequired={true}
            onBlur={handleBlur(`BuyerDetails.${index}.Amount`)}
            onChange={(value) => {
              setFieldValue(`BuyerDetails.${index}.Amount`, value);
            }}
            isInvalid={
              Boolean(getIn(errors, `BuyerDetails.${index}.Amount`)) &&
              getIn(touched, `BuyerDetails.${index}.Amount`)
            }
            errorMsg={getIn(errors, `BuyerDetails.${index}.Amount`)}
          />
        </Col>

        <PreventEditComponent>
          <Col sm={"auto"} className="ps-0">
            <Button
              variant="transparent"
              className="text-greyDark icon-only d-none d-sm-flex"
              onClick={() => onRemove(index)}
            >
              <FASvgIcon icon={faTrash} />
            </Button>
          </Col>
        </PreventEditComponent>
      </Row>
      {index !== values.BuyerDetails.length - 1 && <hr className="d-block d-sm-none my-4" />}
    </>
  );
};
